import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import trueFactsHe from '../../data/trueFacts.json';
import trueFactsEn from '../../data/trueFacts.en.json';
import trueFactsEs from '../../data/trueFacts.es.json';
import trueFactsFr from '../../data/trueFacts.fr.json';
import kidBulb from '../assets/webp/kid-bulb.webp';

const factsByLang: Record<string, string[]> = {
  he: trueFactsHe,
  en: trueFactsEn,
  es: trueFactsEs,
  fr: trueFactsFr,
};

const CARD_GAP = 10;
const PEEK = 25;

const TrueFactsCarousel: React.FC = () => {
  const { t, i18n } = useTranslation();
  const trueFacts = factsByLang[i18n.language] ?? trueFactsEn;
  const isRTL = i18n.language === 'he';

  const shuffledFacts = useMemo(() => {
    const arr = [...trueFacts];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [trueFacts]);

  const outerRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(shuffledFacts.length - 1);
  const [dragOffset, setDragOffset] = useState(0);
  const [isSnapping, setIsSnapping] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const dragOffsetRef = useRef(0);
  const dragActive = useRef(false);
  const dragStartX = useRef(0);
  const dragStartTime = useRef(0);

  useEffect(() => {
    setCurrentIndex(shuffledFacts.length - 1);
  }, [shuffledFacts]);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const update = () => setCardWidth(el.offsetWidth - PEEK - CARD_GAP);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const slideWidth = cardWidth + CARD_GAP;

  const goTo = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, shuffledFacts.length - 1));
      setIsSnapping(true);
      setCurrentIndex(clamped);
      setDragOffset(0);
      dragOffsetRef.current = 0;
    },
    [shuffledFacts],
  );

  const settle = useCallback(() => {
    if (!dragActive.current) return;
    dragActive.current = false;
    setIsDragging(false);
    const dx = dragOffsetRef.current;
    const dt = Date.now() - dragStartTime.current;
    const velocity = Math.abs(dx) / Math.max(dt, 1);
    if (velocity > 0.25 || Math.abs(dx) > cardWidth * 0.28) {
      goTo(dx > 0 ? currentIndex - 1 : currentIndex + 1);
    } else {
      goTo(currentIndex);
    }
  }, [cardWidth, currentIndex, goTo]);

  const startDrag = useCallback((clientX: number) => {
    dragActive.current = true;
    dragStartX.current = clientX;
    dragStartTime.current = Date.now();
    setIsSnapping(false);
    setIsDragging(true);
  }, []);

  const moveDrag = useCallback((clientX: number) => {
    if (!dragActive.current) return;
    const dx = clientX - dragStartX.current;
    dragOffsetRef.current = dx;
    setDragOffset(dx);
  }, []);

  const onTouchStart = (e: React.TouchEvent) => startDrag(e.touches[0].clientX);
  const onTouchMove = (e: React.TouchEvent) => moveDrag(e.touches[0].clientX);
  const onTouchEnd = () => settle();

  const onMouseDown = (e: React.MouseEvent) => startDrag(e.pageX);
  const onMouseMove = (e: React.MouseEvent) => moveDrag(e.pageX);
  const onMouseUp = () => settle();

  const translateX = -currentIndex * slideWidth + PEEK + dragOffset;

  return (
    <div style={{ marginTop: 20 }}>
      {/* Viewport */}
      <div
        ref={outerRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          overflowX: 'hidden',
          overflowY: 'visible',
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'pan-y',
          paddingTop: 20,
          paddingBottom: 16,
          position: 'relative',
        }}
      >
        {/* Sliding strip */}
        <div
          style={{
            display: 'flex',
            gap: CARD_GAP,
            direction: 'ltr',
            transform: `translateX(${translateX}px)`,
            transition: isSnapping
              ? 'transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              : 'none',
            willChange: 'transform',
            userSelect: 'none',
          }}
          onTransitionEnd={() => setIsSnapping(false)}
        >
          {shuffledFacts.map((fact, i) => (
            <div
              key={i}
              style={{
                flexShrink: 0,
                width: cardWidth || 300,
                background: '#E5E7EB',
                borderRadius: 20,
                padding: '4px 6px',
                height: 100,
                boxSizing: 'border-box',
                direction: isRTL ? 'rtl' : 'ltr',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {/* Text */}
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 'bold',
                  color: '#1F2937',
                  lineHeight: 1,
                  textAlign: isRTL ? 'right' : 'left',
                  flex: 1,
                  paddingLeft: 4,
                  paddingTop: 8,
                  paddingRight: 90,
                }}
              >
                {fact}
              </div>
            </div>
          ))}
        </div>

        {/* Yellow badge — fixed, does not slide */}
        <div
          style={{
            position: 'absolute',
            bottom: 4,
            right: 9,
            background: '#F5C842',
            borderRadius: 20,
            padding: '5px 16px',
            fontSize: 13,
            fontWeight: 800,
            color: '#1F2937',
            whiteSpace: 'nowrap',
            zIndex: 2,
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            pointerEvents: 'none',
          }}
        >
          {t('true_facts_title', 'הידעת?')}
        </div>

        {/* Kid image — fixed, does not slide */}
        <img
          src={kidBulb}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            bottom: 16,
            right: -6,
            height: 133,
            width: 'auto',
            objectFit: 'contain',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      </div>
    </div>
  );
};

export default TrueFactsCarousel;
